import { useQuery } from '@tanstack/react-query';

import { YStack } from '@/components/ui/layout/layout';
import {
  APP_SETUP_BY_TEMPLATE,
  AppSetupSteps,
  openFullDiskAccessSettings,
} from '@/features/local-mcp';
import { fullDiskAccessPermissionQueryOptions } from '@/query/permissions';

import { AppConnectionStatus } from './app-connection-status';
import { DevicePermissionBanner } from './device-permission-banner';

import type { ConfigurationContext } from './configuration.types';

// macOS has no prompt for Full Disk Access; System Settings is the only way to grant it
const requestFullDiskAccess = async () => ({
  success: false,
  needsSystemSettings: true,
});

export function FullDiskAccessBanner() {
  const { data: granted } = useQuery(fullDiskAccessPermissionQueryOptions());
  if (granted === undefined) return null;

  return (
    <DevicePermissionBanner
      status={granted ? 'authorized' : 'denied'}
      domain="Full Disk Access"
      domainPlural="files"
      accessName="Full Disk Access"
      onGrantAccess={requestFullDiskAccess}
      onOpenSettings={openFullDiskAccessSettings}
    />
  );
}

export function AppSetupConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const source = context.localSource;
  if (source?.kind !== 'local_mcp' || !source.trustedTemplateId) return null;
  const appSetup = APP_SETUP_BY_TEMPLATE[source.trustedTemplateId];
  if (!appSetup) return null;

  return (
    <YStack className="gap-4 p-6">
      {appSetup.showsConnectionStatus && (
        <AppConnectionStatus
          serverSlug={source.serverSlug}
          appName={appSetup.appName}
        />
      )}
      {appSetup.requiresFullDiskAccess && <FullDiskAccessBanner />}
      <AppSetupSteps appSetup={appSetup} steps={appSetup.installedSteps} />
    </YStack>
  );
}
